import { useCallback } from 'react';
import { Button, Divider, Paper, Stack, Typography, styled } from '@mui/material';

import AccountSummary from '../AccountSummary';
import NoTransactionsCheckbox from '../NoTransactionsCheckbox';
import { UnifiedAccount } from '../../../types/Account.interface';

const Card = styled(Paper, { shouldForwardProp: (prop) => prop !== 'isUnresolved' })<{ isUnresolved: boolean }>(
    ({ theme, isUnresolved }) => ({
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        padding: theme.spacing(2, 3),
        borderRadius: theme.spacing(2),
        ...(isUnresolved && { borderColor: theme.palette.error.main }),
    })
);

interface AccountReviewCardProps {
    account: UnifiedAccount;
    selectedCount: number;
    isWaived: boolean;
    /** Flags the card when Complete is pressed before it has selections or a waiver. */
    isUnresolved?: boolean;
    onReview: (account: UnifiedAccount) => void;
    onWaivedChange: (accountId: string, isWaived: boolean) => void;
}

/** A personal account to review, with its own waiver until any of its transactions are selected. */
export default function AccountReviewCard({
    account,
    selectedCount,
    isWaived,
    isUnresolved = false,
    onReview,
    onWaivedChange,
}: Readonly<AccountReviewCardProps>) {
    const handleReview = useCallback(() => onReview(account), [onReview, account]);
    const handleWaivedChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => onWaivedChange(String(account.id), event.target.checked),
        [onWaivedChange, account.id]
    );

    return (
        <Card variant="outlined" isUnresolved={isUnresolved}>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between">
                <AccountSummary account={account} logoSize={32} />
                <Stack direction="row" spacing={2} alignItems="center">
                    {selectedCount > 0 && (
                        <Typography variant="body2" color="text.secondary">
                            {selectedCount} selected
                        </Typography>
                    )}
                    <Button variant={selectedCount > 0 ? 'outlined' : 'contained'} color="onyx" onClick={handleReview}>
                        {selectedCount > 0 ? 'Add more' : 'Review transactions'}
                    </Button>
                </Stack>
            </Stack>
            {selectedCount === 0 && (
                <>
                    <Divider />
                    <NoTransactionsCheckbox checked={isWaived} onChange={handleWaivedChange} />
                </>
            )}
            {isUnresolved && (
                <Typography variant="body2" color="error">
                    Add transactions or confirm you don&apos;t have any to add.
                </Typography>
            )}
        </Card>
    );
}
